import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_ROUTE, environment, ENVIRONMENTS } from '@/constants';

const { production } = ENVIRONMENTS;

/**
 * Hook to manage Crisp chat widget visibility based on current route
 *
 * Shows Crisp only on:
 * - Authentication screens (/auth/*)
 * - Onboarding screens (/onboarding/*)
 *
 * Hides Crisp on all other routes (when AppTopNav is visible)
 *
 * Note: When users manually open Crisp from AppTopNav (via "Chat with us" button),
 * the chat will remain open until they navigate to a different route or close it manually.
 * The hook only controls visibility on route changes, not on component re-renders.
 */
export const useCrispVisibility = () => {
  const location = useLocation();
  const previousPathnameRef = useRef<string>(location.pathname);
  useEffect(() => {
    // Early return if Crisp is not available
    if (!window.$crisp) {
      return;
    }

    // Only update visibility when route actually changes
    if (previousPathnameRef.current === location.pathname) {
      return;
    }

    previousPathnameRef.current = location.pathname;

    // Check if current route is auth or onboarding
    const isAuthRoute = location.pathname.startsWith(`/${APP_ROUTE.AUTH}/`);
    const isOnboardingRoute =
      location.pathname.startsWith(`/${APP_ROUTE.ONBOARDING}/`) ||
      location.pathname === `/${APP_ROUTE.ONBOARDING}`;
    // Special case: addon support route should show Crisp
    const isAddonSupportRoute =
      location.pathname === `/${APP_ROUTE.ADDON}/${APP_ROUTE.SUPPORT}`;

    if (isAuthRoute || isOnboardingRoute || isAddonSupportRoute) {
      // Show Crisp on auth/onboarding routes
      window.$crisp.push(['do', 'chat:show']);
    } else {
      // Hide Crisp on all other routes (when AppTopNav is visible)
      window.$crisp.push(['do', 'chat:hide']);
    }
  }, [location.pathname]);
};
